/**
 * Shareable demo links (marketing): /demo starts the demo as the technical
 * officer, /demo/<persona> as that persona, and both land on the fleet of
 * the demo club. `?demo=<role>` on any route is the older alias and keeps
 * the route it was put on. Pure, so the mapping is tested without a browser.
 */
import { DEMO_HOME_SLUG } from '@fsv/data';
import type { DemoRole } from '../auth/sessionStore';
import { tenantPath } from './tenantRouting';

/** Path segment after /demo → persona. The empty segment is plain /demo. */
export const DEMO_PERSONA_PATHS: Readonly<Record<string, DemoRole>> = {
  '': 'editor',
  mitglied: 'member',
  technik: 'editor',
  admin: 'admin',
};

/**
 * Links to personas the demo no longer has. The platform manager was taken
 * out (owner: „Platformverwaltung wird in der Demo auf gar keinen Fall
 * benötigt.“), but /demo/plattform and ?demo=platform were shared: they start
 * the plain demo instead of a not-found page.
 */
const RETIRED_PERSONA_PATHS: Readonly<Record<string, DemoRole>> = { plattform: 'editor' };
const RETIRED_ALIASES: Readonly<Record<string, DemoRole>> = { platform: 'editor' };

const ROLES: readonly DemoRole[] = ['member', 'editor', 'admin'];

export interface DemoEntry {
  role: DemoRole;
  /** Where the app continues, including what is left of the query string. */
  path: string;
}

/** The demo club's fleet: where every /demo link lands. */
export const demoHomePath = (): string => tenantPath(DEMO_HOME_SLUG);

/** /demo, /demo/, /demo/<persona> (or a retired one); null for anything else, including an unknown persona. */
export function demoRoleFromPath(pathname: string): DemoRole | null {
  const m = /^\/demo(?:\/([^/]*))?\/?$/.exec(pathname);
  if (!m) return null;
  const segment = decodeURIComponent(m[1] ?? '').toLowerCase();
  if (Object.hasOwn(DEMO_PERSONA_PATHS, segment)) return DEMO_PERSONA_PATHS[segment] ?? null;
  return Object.hasOwn(RETIRED_PERSONA_PATHS, segment) ? (RETIRED_PERSONA_PATHS[segment] ?? null) : null;
}

export function demoEntryFromUrl(pathname: string, search: string): DemoEntry | null {
  const params = new URLSearchParams(search);
  const fromPath = demoRoleFromPath(pathname);
  if (fromPath) {
    params.delete('demo');
    const rest = params.toString();
    return { role: fromPath, path: `${demoHomePath()}${rest ? `?${rest}` : ''}` };
  }
  const alias = params.get('demo');
  const role = alias === null ? null : (ROLES as readonly string[]).includes(alias) ? (alias as DemoRole) : Object.hasOwn(RETIRED_ALIASES, alias) ? (RETIRED_ALIASES[alias] ?? null) : null;
  if (role !== null) {
    params.delete('demo');
    const rest = params.toString();
    return { role, path: `${pathname}${rest ? `?${rest}` : ''}` };
  }
  return null;
}
